
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Trash2,
  Shield,
  ShieldCheck,
  UserCheck,
  UserX,
  Beer,
  LayoutDashboard,
  ClipboardList,
  Users,
  LogOut,
  Menu,
  X,
  Package
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const token = localStorage.getItem("bierz_token");

  const authHeaders = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${API_URL}/api/admin/users`,
        authHeaders
      );

      setUsers(response.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const deleteUser = async (id) => {
    try {
      await axios.delete(
        `${API_URL}/api/admin/users/${id}`,
        authHeaders
      );

      setUsers((prev) =>
        prev.filter((user) => user.id !== id)
      );

      toast.success("Usuário deletado");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao deletar usuário");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/users/${id}/status`,
        { status },
        authHeaders
      );

      fetchUsers();

      toast.success("Status atualizado");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao atualizar status");
    }
  };

  const updateRole = async (id, role) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/users/${id}/role`,
        { role },
        authHeaders
      );

      fetchUsers();

      toast.success("Permissão atualizada");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao atualizar permissão");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("bierz_token");
    window.location.href = "/login";
  };

  const Sidebar = () => (
    <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-black transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 transition-transform duration-300 ease-in-out`}>
      <div className="flex flex-col h-full">
        <div className="p-4 border-b border-[#F59E0B]/30">
          <Link to="/" className="flex items-center space-x-2">
            <Beer className="w-8 h-8 text-[#F59E0B]" />
            <span className="text-xl font-bold text-[#F59E0B]">Bierz Admin</span>
          </Link>
        </div>

        <nav className="flex-1 p-4 space-y-2">
          <Link to="/admin" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-[#F59E0B]/10 hover:text-[#F59E0B] transition-colors">
            <LayoutDashboard className="w-5 h-5" />
            <span>Dashboard</span>
          </Link>

          <Link to="/admin/produtos" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-[#F59E0B]/10 hover:text-[#F59E0B] transition-colors">
            <Package className="w-5 h-5" />
            <span>Produtos</span>
          </Link>

          <Link to="/admin/pedidos" className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-[#F59E0B]/10 hover:text-[#F59E0B] transition-colors">
            <ClipboardList className="w-5 h-5" />
            <span>Pedidos</span>
          </Link>

          <Link to="/admin/usuarios" className="flex items-center space-x-3 px-4 py-3 rounded-lg bg-[#F59E0B]/10 text-[#F59E0B]">
            <Users className="w-5 h-5" />
            <span>Usuários</span>
          </Link>
        </nav>

        <div className="p-4 border-t border-[#F59E0B]/30">
          <button
            onClick={handleLogout}
            className="w-full border border-red-500/50 text-red-500 hover:bg-red-500 hover:text-white rounded-lg py-2 flex items-center justify-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            Sair
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#081028]">
      <Sidebar />

      <div className="md:ml-64">
        <div className="md:hidden flex items-center justify-between p-4 border-b border-[#F59E0B]/20">
          <button onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? (
              <X className="text-white" />
            ) : (
              <Menu className="text-white" />
            )}
          </button>
        </div>

        <div className="p-6 text-white">
          <h1 className="text-4xl font-bold mb-10">
            Usuários
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {users.map((user) => (
              <div
                key={user.id || user.email}
                className="bg-black border border-yellow-500/20 rounded-2xl p-6"
              >
                <h2 className="text-2xl font-bold mb-3">
                  {user.name}
                </h2>

                <div className="flex gap-2 mb-4">
                  <span className="bg-gray-700 px-3 py-1 rounded-full text-sm font-bold">
                    {user.role}
                  </span>

                  <span className="w-7 h-7 rounded-full bg-yellow-500"></span>
                </div>

                <div className="space-y-3 text-gray-200 mb-8">
                  <p>{user.email}</p>
                  <p>{user.phone}</p>
                  <p>{user.address}</p>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() =>
                      updateStatus(user.id, "approved")
                    }
                    className="w-full bg-green-600 hover:bg-green-700 rounded-xl py-3 flex items-center justify-center gap-2"
                  >
                    <UserCheck size={18} />
                    Aprovar
                  </button>

                  <button
                    onClick={() =>
                      updateStatus(user.id, "blocked")
                    }
                    className="w-full bg-red-600 hover:bg-red-700 rounded-xl py-3 flex items-center justify-center gap-2"
                  >
                    <UserX size={18} />
                    Bloquear
                  </button>

                  <button
                    onClick={() =>
                      updateRole(user.id, "admin")
                    }
                    className="w-full bg-purple-600 hover:bg-purple-700 rounded-xl py-3 flex items-center justify-center gap-2"
                  >
                    <ShieldCheck size={18} />
                    Tornar Admin
                  </button>

                  <button
                    onClick={() =>
                      updateRole(user.id, "client")
                    }
                    className="w-full bg-gray-700 hover:bg-gray-800 rounded-xl py-3 flex items-center justify-center gap-2"
                  >
                    <Shield size={18} />
                    Remover Admin
                  </button>

                  <button
                    onClick={() => deleteUser(user.id || user.email)}
                    className="w-full border border-red-600 text-red-500 hover:bg-red-600 hover:text-white rounded-xl py-3 flex items-center justify-center gap-2"
                  >
                    <Trash2 size={18} />
                    Deletar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;
